<template>
  <div class="youngs-slit-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>$干涉条纹间距: Δx = (L/d) · λ$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-ys"><feGaussianBlur stdDeviation="4" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
              
              <!-- 涟漪波动动画 -->
              <radialGradient id="wave-grad" cx="50%" cy="50%" r="50%">
                 <stop offset="0%" :stop-color="currentColor" stop-opacity="0.8" />
                 <stop offset="50%" :stop-color="currentColor" stop-opacity="0" />
                 <stop offset="100%" :stop-color="currentColor" stop-opacity="0.2" />
              </radialGradient>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Light Source (Left Plane Wave) -->
           <rect x="0" y="100" width="40" height="400" :fill="currentColor" opacity="0.3" filter="url(#glow-ys)"/>
           <line x1="20" y1="0" x2="20" y2="600" :stroke="currentColor" stroke-width="2" stroke-dasharray="10,10">
              <animate attributeName="stroke-dashoffset" values="20;0" dur="0.5s" repeatCount="indefinite" />
           </line>

           <!-- Double Slits Barrier -->
           <g transform="translate(150, 0)">
              <rect x="0" y="0" width="15" height="600" fill="#1e293b" />
              
              <!-- Slit 1 (top) -->
              <rect x="0" :y="300 - slitSpacing - 10" width="15" height="20" fill="#020617" />
              <circle cx="15" :cy="300 - slitSpacing" r="150" fill="url(#wave-grad)" opacity="0.4">
                 <animate attributeName="r" values="20; 300" dur="2s" repeatCount="indefinite" />
                 <animate attributeName="opacity" values="0.8; 0" dur="2s" repeatCount="indefinite" />
              </circle>

              <!-- Slit 2 (bottom) -->
              <rect x="0" :y="300 + slitSpacing - 10" width="15" height="20" fill="#020617" />
              <circle cx="15" :cy="300 + slitSpacing" r="150" fill="url(#wave-grad)" opacity="0.4">
                 <animate attributeName="r" values="20; 300" dur="2s" repeatCount="indefinite" />
                 <animate attributeName="opacity" values="0.8; 0" dur="2s" repeatCount="indefinite" />
                 <!-- 错开动画延迟体验相干 -->
                 <animate attributeName="begin" values="0s" />
              </circle>
           </g>

           <!-- Interference Fringes on Screen (Right) -->
           <g transform="translate(680, 50)">
              <rect width="60" height="500" fill="#0f172a" stroke="#334155" stroke-width="4" rx="8" />
              
              <!-- Map discrete fringes calculated by cosine intensity -->
              <g v-for="n in 51" :key="'fr'+n">
                 <rect :x="0" :y="(n-1)*10" width="60" height="10" :fill="currentColor" :opacity="calcFringeIntensity( (n-26) * 10 )" />
              </g>

              <text x="30" y="-15" fill="#94a3b8" font-size="20" font-weight="900" text-anchor="middle">光屏</text>
           </g>

           <!-- Center Reference Line -->
           <line x1="165" y1="300" x2="680" y2="300" stroke="#cbd5e1" stroke-width="2" stroke-dasharray="8,6" />
           
           <!-- Overlay Graph of Intensity Profile -->
           <g transform="translate(560, 50)">
              <path :d="intensityGraphPath" fill="none" :stroke="currentColor" stroke-width="3" filter="url(#glow-ys)"/>
              <line x1="0" y1="0" x2="0" y2="500" stroke="#334155" stroke-width="2" />
           </g>
           
        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">波动干涉机理解析</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前干涉场运算</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>入射波长 λ</span><span class="hl" :style="{color: currentColor}">{{ wavelength }} nm</span></div>
                  <div class="data-row"><span>双缝物理间距 d</span><span class="hl text-purple">{{ (slitSpacing*2).toFixed(1) }} μm</span></div>
                  <div class="data-row"><span>缝到屏距离 L</span><span class="hl">500 mm (模拟系数恒定)</span></div>
                  <div class="data-row" style="background: rgba(56,189,248,0.1)">
                     <span>亮纹间距 Δx</span><span class="hl text-blue">{{ fringeSpacing.toFixed(1) }} mm</span>
                  </div>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>这也许是物理学史上最绝美的实验。杨氏双缝实验彻底粉碎了牛顿的单束粒子微粒说。<br/><br/>
               一束平铺射来的相干光脉冲穿越 A 和 B 两个狭缝后，成为了两个新的点光源。它们释放的子波在到达更远的光屏时，由于路程差(光程差)导致了相位的提前或滞后。<br/><br/>
               **波谷与波谷、波峰与波峰相遇的地方强化成了亮条纹；而波峰与波谷相遇的地方相互抵消变成了暗区。**
               </p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item">
               <label>入射光波长 λ (色彩光谱)</label>
               <!-- Slider specific to visible light spectrum (400nm - 700nm) -->
               <el-slider v-model="wavelength" :min="400" :max="700" :step="5" size="small" :format-tooltip="v => v + ' nm'"></el-slider>
            </div>
            
            <div class="s-item">
               <label>双缝间距 d 半间距系数</label>
               <el-slider v-model="slitSpacing" :min="15" :max="100" :step="1" size="small"></el-slider>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'YoungsDoubleSlitViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      slitSpacing: 40,
      wavelength: 550,
      L: 500 // simulation scale strictly internal
    }
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    // Mapping visible spectrum wavelength to RGB hex
    // Approximation for representation: Purple/Blue -> Green/Yellow -> Red 
    currentColor() {
       let wl = this.wavelength;
       if (wl < 450) return "#8b5cf6"; // Violet
       if (wl < 500) return "#3b82f6"; // Blue
       if (wl < 570) return "#10b981"; // Green
       if (wl < 600) return "#facc15"; // Yellow
       if (wl < 640) return "#f97316"; // Orange
       return "#ef4444"; // Red
    },
    // The fringe width calculation derived
    fringeSpacing() {
       // scaled appropriately for the pixel size in the component
       // Delta_x ~ L * lambda / d
       // Since wavelength relates to real physical dimension, fake scale
       return (this.L * (this.wavelength/1000)) / (this.slitSpacing * 2) * 50; 
    },
    intensityGraphPath() {
       let pts = [];
       for (let y = -250; y <= 250; y+= 5) {
          // The Y on screen 0->500 mapped to -250->250 
          let intValue = this.calcFringeIntensity(y);
          // Map intensity 0->1 to width expanding left (-intValue * 100)
          pts.push(`${-intValue * 100},${y + 250}`);
       }
       return 'M ' + pts.join(' L ');
    }
  },
  methods: {
    detectMode() {
      const kp = (this.knowledgePoint || '').toString();
      if (/红光/.test(kp)) this.wavelength = 680;
      else if (/紫光|蓝光/.test(kp)) this.wavelength = 420;
      else if (/缝间距|变宽|变窄/.test(kp)) this.slitSpacing = 80;
    },
    // Calculates intensity using the standard double slit interference formula:
    // I = I_0 * cos^2((pi * d * y) / (lambda * L))
    calcFringeIntensity(yOffset) {
      // Map simulated scale uniformly
      let scaleD = this.slitSpacing * 2;
      let scaleLambda = this.wavelength / 1000;
      let angle = (Math.PI * scaleD * yOffset) / (scaleLambda * this.L * 30); // 30 is pixel-ratio factor
      return Math.pow(Math.cos(angle), 2);
    }
  }
}
</script>

<style scoped lang="scss">
.youngs-slit-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-purple { color: #a855f7; } .text-blue { color: #38bdf8; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 16px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
